import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Building2, Users, TrendingUp, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import logoWwf from "@/assets/logo-wwf.webp";
import logoSalvationArmy from "@/assets/logo-salvation-army.webp";
import logoBrac from "@/assets/logo-brac.webp";
import logoUnhcr from "@/assets/logo-unhcr.webp";
import logoIfrc from "@/assets/logo-ifrc.webp";
import logoExtinctionRebellion from "@/assets/logo-extinction-rebellion.webp";
import logoOxfam from "@/assets/logo-oxfam.webp";

export const CharitySection = () => {
  const navigate = useNavigate();

  const handleAddCharity = () => {
    navigate('/auth', { state: { defaultTab: 'signup', defaultUserType: 'organization' } });
  };

  const features = [
    {
      icon: Building2,
      title: "Dashboard Access",
      description: "Comprehensive tools to manage your fundraising campaigns",
      accent: "from-primary/90 to-emerald-500/80",
      glow: "bg-primary/30",
      ring: "ring-primary/20",
    },
    {
      icon: Users,
      title: "Engage Supporters",
      description: "Connect with donors in meaningful ways",
      accent: "from-sky-500/90 to-primary/80",
      glow: "bg-sky-400/30",
      ring: "ring-sky-400/20",
    },
    {
      icon: TrendingUp,
      title: "Track Performance",
      description: "Real-time reports and analytics",
      accent: "from-amber-400/90 to-orange-500/80",
      glow: "bg-amber-400/30",
      ring: "ring-amber-400/20",
    },
    {
      icon: Shield,
      title: "Zero Platform Fees",
      description: "Keep 100% of your donations",
      accent: "from-violet-500/90 to-fuchsia-500/80",
      glow: "bg-violet-400/30",
      ring: "ring-violet-400/20",
    },
  ];

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-primary/5 via-background to-primary/10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-5 sm:space-y-6 animate-fade-in">
            <div className="inline-flex items-center gap-3">
              <span className="h-px w-8 bg-primary/60" />
              <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-primary">
                For charities & NGOs
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-foreground">
              Fundraising{" "}
              <span className="font-serif italic font-medium text-primary">built around</span>{" "}
              your cause.
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-muted-foreground leading-relaxed">
              Join the charities and NGOs already raising on FirstGiving — modern tools, real engagement, zero platform fees. You keep what your supporters give.
            </p>
            <Button 
              variant="default" 
              size="lg" 
              className="rounded-full px-6 sm:px-8 mt-2 sm:mt-4 w-full sm:w-auto"
              onClick={handleAddCharity}
            >
              Add Your Charity
            </Button>
          </div>

          {/* Right Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 animate-scale-in">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-card rounded-2xl p-4 sm:p-5 lg:p-6 shadow-card hover:shadow-primary transition-all duration-300 border border-border hover:-translate-y-1 overflow-hidden"
                >
                  {/* decorative corner blob */}
                  <div
                    aria-hidden
                    className={cn(
                      "absolute -top-10 -right-10 w-28 h-28 rounded-full blur-2xl opacity-60 group-hover:opacity-90 transition-opacity",
                      feature.glow
                    )}
                  />

                  {/* icon badge */}
                  <div className="relative mb-3 sm:mb-4 w-12 h-12 sm:w-14 sm:h-14">
                    {/* soft outer halo */}
                    <div
                      className={cn(
                        "absolute inset-0 rounded-2xl blur-md opacity-70 bg-gradient-to-br",
                        feature.accent
                      )}
                    />
                    {/* tilted back plate */}
                    <div
                      className={cn(
                        "absolute inset-0 rounded-2xl rotate-6 bg-gradient-to-br opacity-40",
                        feature.accent
                      )}
                    />
                    {/* main badge */}
                    <div
                      className={cn(
                        "relative w-full h-full rounded-2xl bg-gradient-to-br flex items-center justify-center ring-1 shadow-lg group-hover:scale-105 group-hover:-rotate-3 transition-transform duration-300",
                        feature.accent,
                        feature.ring
                      )}
                    >
                      {/* inner gloss */}
                      <div className="absolute inset-x-1 top-1 h-1/2 rounded-t-xl bg-white/25 blur-[2px]" />
                      <Icon className="relative w-6 h-6 sm:w-7 sm:h-7 text-white drop-shadow-sm" strokeWidth={2.25} />
                      {/* small accent dot */}
                      <span className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-white shadow ring-2 ring-background" />
                    </div>
                  </div>

                  <h3 className="relative text-base sm:text-lg font-semibold text-foreground mb-1 sm:mb-2">
                    {feature.title}
                  </h3>
                  <p className="relative text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Charity Logos Section */}
        <div className="mt-10 sm:mt-12 lg:mt-16">
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-4 sm:gap-6 lg:gap-8 items-center justify-items-center">
            <img 
              src={logoWwf} 
              alt="WWF"
              width="64"
              height="64"
              loading="lazy"
              className="h-10 sm:h-12 lg:h-16 w-auto object-contain hover:grayscale transition-all duration-300 opacity-100 hover:opacity-70"
            />
            <img 
              src={logoSalvationArmy} 
              alt="The Salvation Army"
              width="64"
              height="64"
              loading="lazy"
              className="h-10 sm:h-12 lg:h-16 w-auto object-contain hover:grayscale transition-all duration-300 opacity-100 hover:opacity-70"
            />
            <img 
              src={logoBrac} 
              alt="BRAC"
              width="64"
              height="64"
              loading="lazy"
              className="h-10 sm:h-12 lg:h-16 w-auto object-contain hover:grayscale transition-all duration-300 opacity-100 hover:opacity-70"
            />
            <img 
              src={logoUnhcr} 
              alt="UNHCR"
              width="64"
              height="64"
              loading="lazy"
              className="h-10 sm:h-12 lg:h-16 w-auto object-contain hover:grayscale transition-all duration-300 opacity-100 hover:opacity-70"
            />
            <img 
              src={logoIfrc} 
              alt="IFRC"
              width="64"
              height="64"
              loading="lazy"
              className="h-10 sm:h-12 lg:h-16 w-auto object-contain hover:grayscale transition-all duration-300 opacity-100 hover:opacity-70"
            />
            <img 
              src={logoExtinctionRebellion} 
              alt="Extinction Rebellion"
              width="64"
              height="64"
              loading="lazy"
              className="h-10 sm:h-12 lg:h-16 w-auto object-contain hover:grayscale transition-all duration-300 opacity-100 hover:opacity-70"
            />
            <img 
              src={logoOxfam} 
              alt="Oxfam"
              width="64"
              height="64"
              loading="lazy"
              className="h-10 sm:h-12 lg:h-16 w-auto object-contain hover:grayscale transition-all duration-300 opacity-100 hover:opacity-70 col-span-3 sm:col-span-1"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
